// DashboardHelpers.js - Handles data storage and chart configuration logic

const API_URL = "https://chlorocast-517789008911.asia-southeast1.run.app/data";
const HOURLY_API_URL = "https://chlorocast-517789008911.asia-southeast1.run.app/api/hourlydata";

// Standardized Chart Colors
const CHART_COLORS = {
  primary: '#015486',
  primaryLight: 'rgba(1, 84, 134, 0.1)',
  secondary: '#8FD8FF',
  turbidity: '#f97316',
  ph: '#ec4899',
  conductivity: '#a855f7',
  lime: '#eab308',
  alum: '#06b6d4',
  wqiGood: '#1e40af',
  wqiBg: '#e2e8f0'
};

// Extracted static hourly fallback data
const DEFAULT_HOURLY_DATA = {
  labels: ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'],
  chlorineActual: [15, 16, 15.5, 15, 14.5, 14, 15, 17, 18, 16.5, 16, 15.5, 15.8],
  chlorinePredicted: [15.2, 16.1, 15.6, 15.1, 14.6, 14.2, 15.1, 17.3, 17.8, 16.7, 16.2, 15.6, 16.1],
  rawTurbidity: [5.2, 5.1, 5.3, 5.0, 4.9, 5.2, 5.5, 5.4, 5.6, 5.5, 5.8, 5.4, 5.2],
  rawPh: [7.1, 7.2, 7.1, 7.0, 7.1, 6.9, 6.4, 6.8, 7.0, 7.1, 7.2, 7.1, 7.1],
  rawConductivity: [300, 302, 301, 299, 298, 305, 310, 308, 305, 303, 306, 304, 302],
  limeDosage: [10, 11, 10.5, 10, 10.2, 10.8, 11.5, 11, 10.6, 10.5, 10.8, 10.7, 10.5],
  alumDosage: [8, 8.5, 8.2, 8, 7.9, 8.3, 8.8, 8.6, 8.5, 8.4, 8.7, 8.5, 8.2],
  intakeFlow: [1200, 1250, 1210, 1190, 1180, 1200, 1220, 1250, 1300, 1280, 1260, 1230, 1200]
};

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatHourLabel = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value ?? '';
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

async function getHourlyData() {
  const response = await fetch(HOURLY_API_URL);

  if (!response.ok) {
    throw new Error(`Failed to fetch hourly data: ${response.status}`);
  }

  const rows = await response.json();

  if (!Array.isArray(rows) || rows.length === 0) {
    return DEFAULT_HOURLY_DATA;
  }

  const sortedRows = [...rows].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return {
    timestamps: sortedRows.map((row) => String(row.timestamp ?? '')),
    labels: sortedRows.map((row) => formatHourLabel(row.timestamp)),
    chlorineActual: sortedRows.map((row) => toNumber(row.chlorine_pre_l_m) + toNumber(row.chlorine_post_l_m)),
    chlorinePredicted: sortedRows.map((row) => {
      const predicted = toNumber(row.predicted_chlorine, NaN);
      if (Number.isFinite(predicted)) {
        return predicted;
      }

      return toNumber(row.chlorine_pre_l_m) + toNumber(row.chlorine_post_l_m);
    }),
    rawTurbidity: sortedRows.map((row) => toNumber(row.raw_water_tur)),
    rawPh: sortedRows.map((row) => toNumber(row.ph)),
    rawConductivity: sortedRows.map((row) => toNumber(row.con)),
    limeDosage: sortedRows.map((row) => toNumber(row.prelime_l_m) + toNumber(row.post_lime_sph1_l_m) + toNumber(row.post_lime_sph2_l_m)),
    alumDosage: sortedRows.map((row) => toNumber(row.aetator_flow_rate_l_m) + toNumber(row.d_chamber_flow_rate_l_m)),
    intakeFlow: sortedRows.map((row) => toNumber(row.raw_water_m3_h)),
    finalSph2Turbidity: sortedRows.map((row) => toNumber(row.final_water_sph2_tur)),
    finalSph2Ph: sortedRows.map((row) => toNumber(row.final_water_sph2_ph)),
    finalSph2Conductivity: sortedRows.map((row) => toNumber(row.final_water_sph2_co)),
    finalSph2ResidualChlorine: sortedRows.map((row) => toNumber(row.final_water_sph2_rcl)),
  };
}

// Fetch the last 7 days of water data from the API and format it for our app
async function getLast7DaysRawWaterTur() {
  const response = await fetch(API_URL);

  if (!response.ok) {
    throw new Error(`Failed to fetch data: ${response.status}`);
  }

  const data = await response.json();

  return data
    .filter((entry) => entry.raw_water_tur !== null)
    .sort((a, b) => new Date(b.date.value) - new Date(a.date.value))
    .slice(0, 7)
    .reverse()
    .map((entry) => ({
      date: entry.date.value,
      raw_water_tur: entry.raw_water_tur,
      raw_water_m3_h: entry.raw_water_m3_h,
      ph: entry.ph,
      con: entry.con,

      // Chemicals (Lime & Alum)
      prelime_ppm: entry.prelime_ppm,
      prelime_l_m: entry.prelime_l_m,
      alum_ppm: entry.alum_ppm,
            
      // Flow Rates
      aetator_flow_rate_l_m: entry.aetator_flow_rate_l_m,
      d_chamber_flow_rate_l_m: entry.d_chamber_flow_rate_l_m,

      // Post Lime
      pl_sph_1_ppm: entry.post_lime_sph_1_ppm,
      pl_sph1_l_m: entry.post_lime_sph1_l_m,
      pl_sph2_ppm: entry.post_lime_sph2_ppm,
      pl_sph2_l_m: entry.post_lime_sph2_l_m,

      // Chlorine
      chlorine_pre_ppm: entry.chlorine_pre_ppm,
      chlorine_pre_l_m: entry.chlorine_pre_l_m,
      chlorine_post_ppm: entry.chlorine_post_ppm,
      chlorine_post_l_m: entry.chlorine_post_l_m,

      // Settled Water 
      sw_pul_tube_tur: entry.settled_water_pul_tube_tur,
      sw_pul_tube_ph: entry.settled_water_pul_tube_ph,
      sw_pulsator_tur: entry.settled_water_pulsator_tur,
      sw_pulsator_ph: entry.settled_water_pulsator_ph,
      sw_cf1_tur: entry.settled_water_cf1_tur,
      sw_cf1_ph: entry.settled_water_cf1_ph,
      sw_cf2_tur: entry.settled_water_cf2_tur,
      sw_cf2_ph: entry.settled_water_cf2_ph,
      sw_pt1_tur: entry.settled_water_pt1_tur,
      sw_pt1_ph: entry.settled_water_pt1_ph,
      sw_pt2_tur: entry.settled_water_pt2_tur,
      sw_pt2_ph: entry.settled_water_pt2_ph,
    
      // Filtered & Final Water 
      filtered_water_tur: entry.filtered_water_tur,
      filtered_water_ph: entry.filtered_water_ph,
      fw_sph1_tur: entry.final_water_sph1_tur,
      fw_sph1_ph: entry.final_water_sph1_ph,
      fw_sph1_co: entry.final_water_sph1_co,
      fw_sph1_rcl: entry.final_water_sph1_rcl,
      fw_sph2_tur: entry.final_water_sph2_tur,
      fw_sph2_ph: entry.final_water_sph2_ph,
      fw_sph2_co: entry.final_water_sph2_co,
      fw_sph2_rcl: entry.final_water_sph2_rcl
    }));
}

// Combine our fetched API data with our static fallback data so the charts have everything they need
async function initMockData() {
  const hourlyData = await getHourlyData();
  const last7Days = await getLast7DaysRawWaterTur();
  
  const dates = last7Days.map((entry) => entry.date);
  const turValues = last7Days.map((entry) => entry.raw_water_tur);
  const rawFlows = last7Days.map((entry) => entry.raw_water_m3_h);
  const phValues = last7Days.map((entry) => entry.ph);
  const conValues = last7Days.map((entry) => entry.con);
  
  // Chemicals (Lime & Alum)
  const prelimePpmValues = last7Days.map((entry) => entry.prelime_ppm);
  const prelimeLmValues = last7Days.map((entry) => entry.prelime_l_m);
  const alumPpmValues = last7Days.map((entry) => entry.alum_ppm);

  // Flow Rates & Total Alum Dosage
  const aetatorFlowValues = last7Days.map((entry) => entry.aetator_flow_rate_l_m);
  const dChamberFlowValues = last7Days.map((entry) => entry.d_chamber_flow_rate_l_m);
  const alumDosageValues = last7Days.map((entry) => 
    (entry.aetator_flow_rate_l_m || 0) + (entry.d_chamber_flow_rate_l_m || 0)
  );

  // Post Lime & Total Lime Dosage
  const plSph1PpmValues = last7Days.map((entry) => entry.pl_sph_1_ppm);
  const plSph2PpmValues = last7Days.map((entry) => entry.pl_sph2_ppm);
  const limeDosageValues = last7Days.map((entry) => 
    (entry.pl_sph1_l_m || 0) + (entry.pl_sph2_l_m || 0)
  );

  // Chlorine
  const chlorinePrePpmValues = last7Days.map((entry) => entry.chlorine_pre_ppm);
  const chlorinePreLmValues = last7Days.map((entry) => entry.chlorine_pre_l_m);
  const chlorinePostPpmValues = last7Days.map((entry) => entry.chlorine_post_ppm);
  const chlorinePostLmValues = last7Days.map((entry) => entry.chlorine_post_l_m);

  // Settled Water
  const swPulTubeTurValues = last7Days.map((entry) => entry.sw_pul_tube_tur);
  const swPulTubePhValues = last7Days.map((entry) => entry.sw_pul_tube_ph);
  const swPulsatorTurValues = last7Days.map((entry) => entry.sw_pulsator_tur);
  const swPulsatorPhValues = last7Days.map((entry) => entry.sw_pulsator_ph);
  const swCf1TurValues = last7Days.map((entry) => entry.sw_cf1_tur);
  const swCf1PhValues = last7Days.map((entry) => entry.sw_cf1_ph);
  const swCf2TurValues = last7Days.map((entry) => entry.sw_cf2_tur);
  const swCf2PhValues = last7Days.map((entry) => entry.sw_cf2_ph); 
  const swPt1TurValues = last7Days.map((entry) => entry.sw_pt1_tur);
  const swPt1PhValues = last7Days.map((entry) => entry.sw_pt1_ph);
  const swPt2TurValues = last7Days.map((entry) => entry.sw_pt2_tur);
  const swPt2PhValues = last7Days.map((entry) => entry.sw_pt2_ph);
  
  // Filtered & Final Water
  const filteredTurValues = last7Days.map((entry) => entry.filtered_water_tur);
  const filteredPhValues = last7Days.map((entry) => entry.filtered_water_ph);
  const fwSph1TurValues = last7Days.map((entry) => entry.fw_sph1_tur);
  const fwSph1PhValues = last7Days.map((entry) => entry.fw_sph1_ph);
  const fwSph1CoValues = last7Days.map((entry) => entry.fw_sph1_co);
  const fwSph1RclValues = last7Days.map((entry) => entry.fw_sph1_rcl);
  const fwSph2TurValues = last7Days.map((entry) => entry.fw_sph2_tur);
  const fwSph2PhValues = last7Days.map((entry) => entry.fw_sph2_ph);
  const fwSph2CoValues = last7Days.map((entry) => entry.fw_sph2_co);
  const fwSph2RclValues = last7Days.map((entry) => entry.fw_sph2_rcl);

  console.log("Dashboard data successfully loaded!");

  return {
    hourly: hourlyData,
    daily: {
      labels: dates,            
      chlorineActual: [362, 372, 384, 379, 364, 355, 357],
      chlorinePredicted: [360, 374, 381, 376, 367, 352, 360],
      rawTurbidity: turValues,  
      rawPh: phValues,          
      rawConductivity: conValues, 
      limeDosage: limeDosageValues, 
      alumDosage: alumDosageValues, 
      intakeFlow: rawFlows,

      prelimePpm: prelimePpmValues,
      prelimeLm: prelimeLmValues,
      alumPpm: alumPpmValues,
      
      aetatorFlow: aetatorFlowValues,
      dChamberFlow: dChamberFlowValues,

      plSph1Ppm: plSph1PpmValues,
      plSph2Ppm: plSph2PpmValues,
      
      chlorinePrePpm: chlorinePrePpmValues,
      chlorinePreLm: chlorinePreLmValues,
      chlorinePostPpm: chlorinePostPpmValues,
      chlorinePostLm: chlorinePostLmValues,

      swPulTubeTur: swPulTubeTurValues,
      swPulTubePh: swPulTubePhValues,
      swPulsatorTur: swPulsatorTurValues,
      swPulsatorPh: swPulsatorPhValues,
      swCf1Tur: swCf1TurValues,
      swCf1Ph: swCf1PhValues,
      swCf2Tur: swCf2TurValues,
      swCf2Ph: swCf2PhValues,
      swPt1Tur: swPt1TurValues,
      swPt1Ph: swPt1PhValues,
      swPt2Tur: swPt2TurValues,
      swPt2Ph: swPt2PhValues,
      
      filteredTur: filteredTurValues,
      filteredPh: filteredPhValues,
      fwSph1Tur: fwSph1TurValues,
      fwSph1Ph: fwSph1PhValues,
      fwSph1Co: fwSph1CoValues,
      fwSph1Rcl: fwSph1RclValues,
      fwSph2Tur: fwSph2TurValues,
      fwSph2Ph: fwSph2PhValues,
      fwSph2Co: fwSph2CoValues,
      fwSph2Rcl: fwSph2RclValues
    }
  };
}

// A small helper to safely get numbers for our charts
const getData = (mockData, type, key) =>
  mockData[type] ? mockData[type][key] : mockData['hourly'][key];

// A small helper to safely get time labels for our charts
const getLabels = (mockData, type) =>
  mockData[type] ? mockData[type].labels : mockData['hourly'].labels;

const buildKpiData = (hourlyData) => {
  const lastIndex = Math.max((hourlyData?.rawTurbidity?.length || 1) - 1, 0);
  const prevIndex = Math.max(lastIndex - 1, 0);

  const currentTur = toNumber(hourlyData?.rawTurbidity?.[lastIndex]);
  const previousTur = toNumber(hourlyData?.rawTurbidity?.[prevIndex]);
  const currentPh = toNumber(hourlyData?.rawPh?.[lastIndex]);
  const currentCond = toNumber(hourlyData?.rawConductivity?.[lastIndex]);
  const currentChlorine = toNumber(hourlyData?.chlorineActual?.[lastIndex]);
  const predictedChlorine = toNumber(hourlyData?.chlorinePredicted?.[lastIndex], currentChlorine);

  const diff = currentTur - previousTur;
  const sign = diff > 0 ? '+' : '';
  const turbiditySubText = `${sign}${diff.toFixed(1)} from last hour`;
  const turbiditySubColor = diff > 0 ? 'text-red' : diff < 0 ? 'text-green' : 'text-gray';

  const phSafe = currentPh >= 6.5 && currentPh <= 8.5;

  return {
    rawTurbidity: {
      value: currentTur.toFixed(1),
      unit: 'NTU',
      subText: turbiditySubText,
      subColor: turbiditySubColor,
    },
    rawWaterPh: {
      value: currentPh.toFixed(1),
      unit: '',
      subText: phSafe ? 'Safe Range (6.5-8.5)' : 'Outside Safe Range',
      subColor: phSafe ? 'text-green' : 'text-red',
    },
    conductivity: {
      value: Math.round(currentCond).toString(),
      unit: 'µS/cm',
      subText: currentCond > 120 ? 'Unstable' : 'Stable',
      subColor: 'text-gray',
    },
    chlorineDosage: {
      value: currentChlorine.toFixed(1),
      recommended: predictedChlorine.toFixed(1),
      unit: 'kg/h',
    },
  };
};

const buildWqiMetrics = (hourlyData) => {
  const lastIndex = Math.max((hourlyData?.finalSph2Turbidity?.length || 1) - 1, 0);

  const finalTurbidity = toNumber(hourlyData?.finalSph2Turbidity?.[lastIndex]);
  const finalPh = toNumber(hourlyData?.finalSph2Ph?.[lastIndex]);
  const finalConductivity = toNumber(hourlyData?.finalSph2Conductivity?.[lastIndex]);
  const residualChlorine = toNumber(hourlyData?.finalSph2ResidualChlorine?.[lastIndex]);

  // Weighted WQI model based on final water indicators.
  const qTur = clamp(100 * (1 - finalTurbidity / 5), 0, 100);
  const qPh = clamp(100 - (Math.abs(finalPh - 7) / 1.5) * 100, 0, 100);
  const qCond = clamp(100 * (1 - finalConductivity / 750), 0, 100);
  const qRcl = clamp(100 - (Math.abs(residualChlorine - 0.5) / 0.5) * 100, 0, 100);

  const wqi = Math.round(0.35 * qTur + 0.2 * qPh + 0.2 * qCond + 0.25 * qRcl);
  const safeWqi = clamp(wqi, 0, 100);

  return {
    value: safeWqi,
    max: 100,
    status: safeWqi >= 80 ? 'SAFE' : 'WARNING',
    details: {
      finalTurbidity: `${finalTurbidity.toFixed(1)} NTU`,
      finalPH: finalPh.toFixed(1),
      chlorineResidual: `${residualChlorine.toFixed(1)} ppm`,
    },
  };
};

const buildComparativeData = (hourlyData) => {
  const timestamps = hourlyData?.timestamps || [];
  const turbidity = hourlyData?.rawTurbidity || [];
  const ph = hourlyData?.rawPh || [];
  const conductivity = hourlyData?.rawConductivity || [];
  const labels = hourlyData?.labels || [];

  const dayMap = new Map();

  timestamps.forEach((stamp, index) => {
    const dayKey = String(stamp).split(' ')[0] || '';
    if (!dayKey) {
      return;
    }

    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, {
        turSum: 0,
        phSum: 0,
        conSum: 0,
        count: 0,
      });
    }

    const agg = dayMap.get(dayKey);
    agg.turSum += toNumber(turbidity[index]);
    agg.phSum += toNumber(ph[index]);
    agg.conSum += toNumber(conductivity[index]);
    agg.count += 1;
  });

  const dayKeys = Array.from(dayMap.keys()).sort();
  const todayKey = dayKeys[dayKeys.length - 1];
  const yesterdayKey = dayKeys[dayKeys.length - 2];

  const todayAgg = todayKey ? dayMap.get(todayKey) : null;
  const yesterdayAgg = yesterdayKey ? dayMap.get(yesterdayKey) : null;

  const avg = (sum, count) => (count > 0 ? sum / count : 0);

  const todayTur = avg(todayAgg?.turSum || 0, todayAgg?.count || 0);
  const todayPh = avg(todayAgg?.phSum || 0, todayAgg?.count || 0);
  const todayCon = avg(todayAgg?.conSum || 0, todayAgg?.count || 0);

  const yesterdayTur = avg(yesterdayAgg?.turSum || 0, yesterdayAgg?.count || 0);
  const yesterdayPh = avg(yesterdayAgg?.phSum || 0, yesterdayAgg?.count || 0);
  const yesterdayCon = avg(yesterdayAgg?.conSum || 0, yesterdayAgg?.count || 0);

  const lastIndex = Math.max(turbidity.length - 1, 0);
  const prevIndex = Math.max(lastIndex - 1, 0);

  return {
    todayVsYesterday: {
      todayLabel: todayKey || 'Today',
      yesterdayLabel: yesterdayKey || 'Yesterday',
      metrics: [
        { name: 'Turbidity', today: `${todayTur.toFixed(1)} NTU`, yesterday: `${yesterdayTur.toFixed(1)} NTU` },
        { name: 'pH', today: todayPh.toFixed(1), yesterday: yesterdayPh.toFixed(1) },
        { name: 'Conductivity', today: `${Math.round(todayCon)} µS/cm`, yesterday: `${Math.round(yesterdayCon)} µS/cm` },
      ],
    },
    hourVsPrevious: {
      currentHourLabel: labels[lastIndex] || 'Last Hour',
      previousHourLabel: labels[prevIndex] || 'Previous Hour',
      metrics: [
        { name: 'Turbidity', current: `${toNumber(turbidity[lastIndex]).toFixed(1)} NTU`, previous: `${toNumber(turbidity[prevIndex]).toFixed(1)} NTU` },
        { name: 'pH', current: toNumber(ph[lastIndex]).toFixed(1), previous: toNumber(ph[prevIndex]).toFixed(1) },
        { name: 'Conductivity', current: `${Math.round(toNumber(conductivity[lastIndex]))} µS/cm`, previous: `${Math.round(toNumber(conductivity[prevIndex]))} µS/cm` },
      ],
    },
  };
};

const buildAlertsData = (hourlyData) => {
  const labels = hourlyData?.labels || [];
  const lastIndex = Math.max((labels.length || 1) - 1, 0);
  const prevIndex = Math.max(lastIndex - 1, 0);

  const currentTur = toNumber(hourlyData?.rawTurbidity?.[lastIndex]);
  const previousTur = toNumber(hourlyData?.rawTurbidity?.[prevIndex]);
  const currentPh = toNumber(hourlyData?.rawPh?.[lastIndex]);
  const previousPh = toNumber(hourlyData?.rawPh?.[prevIndex]);
  const currentCon = toNumber(hourlyData?.rawConductivity?.[lastIndex]);
  const previousCon = toNumber(hourlyData?.rawConductivity?.[prevIndex]);
  const currentRcl = toNumber(hourlyData?.finalSph2ResidualChlorine?.[lastIndex]);

  const hourLabel = labels[lastIndex] || 'latest hour';
  const alerts = [];

  if (currentTur > 5) {
    alerts.push({
      type: 'red',
      title: 'High Turbidity',
      desc: `${currentTur.toFixed(1)} NTU at ${hourLabel}`,
    });
  }

  if (currentPh < 6.5 || currentPh > 8.5) {
    alerts.push({
      type: 'red',
      title: 'pH Out Of Range',
      desc: `pH ${currentPh.toFixed(1)} at ${hourLabel}`,
    });
  }

  if (currentCon > 750) {
    alerts.push({
      type: 'yellow',
      title: 'High Conductivity',
      desc: `${Math.round(currentCon)} µS/cm at ${hourLabel}`,
    });
  }

  if (currentRcl < 0.2 || currentRcl > 1.0) {
    alerts.push({
      type: 'yellow',
      title: 'Residual Chlorine Drift',
      desc: `${currentRcl.toFixed(1)} ppm at ${hourLabel}`,
    });
  }

  if (Math.abs(currentTur - previousTur) > 1.0) {
    alerts.push({
      type: 'yellow',
      title: 'Turbidity Jump',
      desc: `Changed by ${(currentTur - previousTur).toFixed(1)} NTU from previous hour`,
    });
  }

  if (Math.abs(currentPh - previousPh) > 0.4) {
    alerts.push({
      type: 'yellow',
      title: 'pH Shift',
      desc: `Changed by ${(currentPh - previousPh).toFixed(1)} from previous hour`,
    });
  }

  if (Math.abs(currentCon - previousCon) > 100) {
    alerts.push({
      type: 'yellow',
      title: 'Conductivity Shift',
      desc: `Changed by ${Math.round(currentCon - previousCon)} µS/cm from previous hour`,
    });
  }

  return alerts;
};

// Build the final settings and data structures that Chart.js needs to draw the graphs
export const getChartConfigs = async (filters) => {
  const mockData = await initMockData();
  const { chlorine, rawWater, chemical, intake } = filters;
  const wqiMetrics = buildWqiMetrics(mockData.hourly);
  const comparativeData = buildComparativeData(mockData.hourly);
  const alertsData = buildAlertsData(mockData.hourly);

  return {
    kpiData: buildKpiData(mockData.hourly),
    wqiMetrics,
    finalQualityData: {
      status: wqiMetrics.status,
      wqiText: `${wqiMetrics.value}/${wqiMetrics.max}`,
    },
    comparativeData,
    alertsData,
    chlorineData: {
      labels: getLabels(mockData, chlorine),
      datasets: [
        {
          label: 'Actual Dosage',
          data: getData(mockData, chlorine, 'chlorineActual'),
          borderColor: CHART_COLORS.primary,
          backgroundColor: CHART_COLORS.primaryLight,
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Predicted',
          data: getData(mockData, chlorine, 'chlorinePredicted'),
          borderColor: CHART_COLORS.secondary,
          borderDash: [5, 5],
          tension: 0.4,
        }
      ]
    },
    rawWaterData: {
      labels: getLabels(mockData, rawWater),
      datasets: [
        { label: 'Turbidity (NTU)', data: getData(mockData, rawWater, 'rawTurbidity'), borderColor: CHART_COLORS.turbidity, yAxisID: 'y' },
        { label: 'pH', data: getData(mockData, rawWater, 'rawPh'), borderColor: CHART_COLORS.ph, yAxisID: 'y1' },
        { label: 'Conductivity', data: getData(mockData, rawWater, 'rawConductivity'), borderColor: CHART_COLORS.conductivity, display: false }
      ]
    },
    chemicalData: {
      labels: getLabels(mockData, chemical),
      datasets: [
        { label: 'Lime (ppm)', data: getData(mockData, chemical, 'limeDosage'), borderColor: CHART_COLORS.lime },
        { label: 'Alum (ppm)', data: getData(mockData, chemical, 'alumDosage'), borderColor: CHART_COLORS.alum }
      ]
    },
    intakeData: {
      labels: getLabels(mockData, intake).slice(-6),
      datasets: [{
        label: 'Flow (m³/h)',
        data: getData(mockData, intake, 'intakeFlow').slice(-6),
        backgroundColor: CHART_COLORS.primary,
        borderRadius: 4,
      }]
    },
    wqiData: {
      labels: ['WQI', 'Remaining'],
      datasets: [{
        data: [wqiMetrics.value, Math.max(0, wqiMetrics.max - wqiMetrics.value)],
        backgroundColor: [CHART_COLORS.wqiGood, CHART_COLORS.wqiBg],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
        borderRadius: 10,
      }]
    }
  };
};